/**
 * The single checkbox primitive for every app (BL-1242, ratifying the
 * `Checkbox` row of the ADR 030 Canon).
 *
 * The merge collapses two implementations of the same job — bidder-web's
 * `Checkbox` (the canonical base per ADR 030 § 3b: it extends
 * `InputHTMLAttributes`, so native accessibility attributes pass straight
 * through) and admin-web's `Checkbox` (which contributed `indeterminate`).
 *
 * WHY the core renders a real native input and draws only OPTIONALLY: unlike
 * `RadioButton`, where both apps hand-drew their control, these two differ in
 * CONSTRUCTION, not just in values. Admin makes the input transparent (laid over
 * its box since BL-1897) and paints a 16px box with a lucide tick; bidder lets
 * the BROWSER paint the input and
 * only widens the hit region to the 44x44 mobile tap-target minimum. ADR 030
 * § 3b says the merge collapses the implementation, not the appearance — so
 * making the core custom-draw everything would have been a visual regression on
 * the buyer site, and making it never draw would have been one on admin. The
 * core therefore owns the real control and treats the DRAWN LAYER as a dialect
 * slot: supply `box` and you get admin's construction, omit it and you get
 * bidder's.
 *
 * WHY a `dialect` object rather than a pile of `className` props: ADR 030's
 * foundation is shared but its dialects are deliberately different, and
 * `sharing.md` § 1 caps a per-app skin at a 2–5 line wrapper. One frozen object
 * of class names per app keeps the appearance a piece of DATA the skin declares
 * once, so the wrapper stays a passthrough instead of re-deriving styling on
 * every render.
 *
 * WHY `indeterminate` needs an effect: it is a DOM *property* with no HTML
 * attribute, so React cannot set it declaratively. Admin's local implementation
 * never set it at all — it drew a white bar and left the input reporting
 * "unchecked", so a screen reader announced a half-selected table header as
 * plainly unselected. The ref effect below is what makes the tri-state real
 * rather than cosmetic.
 */
'use client';

import clsx from 'clsx';
import {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    type InputHTMLAttributes,
    type ReactNode,
} from 'react';

/**
 * Per-app appearance binding — class names only, no structure.
 *
 * Every slot is optional: a consumer with no dialect gets a bare, accessible
 * native checkbox, which is the right default for a fourth app that has not
 * declared one yet.
 */
export interface CheckboxDialect {
    /**
     * Wrapper element. `'label'` makes the whole row toggle the input — required
     * when the input is visually hidden and a drawn box stands in for it.
     * `'span'` leaves labelling to the caller, which is what a consumer whose
     * call sites already sit inside their own `<label>` needs (nesting labels is
     * invalid HTML and breaks click-to-toggle). Defaults to `'span'`.
     */
    rootAs?: 'label' | 'span';
    /** The wrapping element — layout, hit area, cursor. */
    root?: string;
    /**
     * Classes on the native input. Put `sr-only` here to hand painting to `box`;
     * leave the input visible to let the browser paint the control itself.
     */
    input?: string;
    /**
     * The drawn box. Supplying this switches the decorative layer ON — omit it
     * and the native input is the only thing rendered.
     *
     * Do NOT put a state-dependent property here. `boxUnchecked` and `boxChecked`
     * are mutually exclusive by construction; `box` is not, so a `border-color`
     * left here would collide with the checked one at equal CSS specificity and
     * the later-emitted utility would win regardless of the order they appear in
     * the class attribute. That shipped a genuinely grey "selected" ring on
     * RadioButton's first draft (BL-1243).
     */
    box?: string;
    /** Applied to the box only when neither checked nor indeterminate. */
    boxUnchecked?: string;
    /** Applied to the box when checked OR indeterminate — both are "on" states. */
    boxChecked?: string;
    /** Glyph rendered inside the box when checked. */
    checkMark?: ReactNode;
    /** The dash drawn when indeterminate and not checked. */
    indeterminateMark?: string;
}

/**
 * The root's structure, owned by the core so every dialect inherits it
 * (BUG-2223): one line of the SURROUNDING text tall, with the control centred
 * in it. That puts the box on the middle of its label's first line in any row,
 * `items-center` or `items-start`, at any text size, with no per-call-site
 * nudge. `1lh` is that line's height (ui-standards.md: an arbitrary value that
 * is a measurement); a browser without the unit drops the declaration and the
 * root falls back to the box's own height. A dialect's `min-h-*` (bidder's 44px
 * tap target) still wins over it.
 */
const ROOT_STRUCTURE = 'h-[1lh] shrink-0 items-center';

/**
 * The display a dialect-less root gets. A plain inline span sat its input on
 * the text baseline inside the line box, a few pixels off centre whatever the
 * row's alignment (BUG-2223). As a top-aligned inline flex box exactly one line
 * tall, it fills the line box instead. A dialect that names its own `root`
 * keeps its own display: admin's block `flex` is what lets a table cell centre
 * the box.
 */
const DEFAULT_ROOT_DISPLAY = 'inline-flex align-top';

/**
 * Props for {@link Checkbox}. Extends the native input attributes, so any
 * accessibility or form attribute a call site passes reaches the real control.
 */
export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    /**
     * Renders the half-selected state and sets the native `indeterminate` DOM
     * property. Ignored for the drawn dash when `checked` is also true.
     */
    indeterminate?: boolean;
    /**
     * Classes on the wrapping element. `className` stays on the INPUT, matching
     * the native shape callers of an `InputHTMLAttributes`-typed component expect.
     */
    rootClassName?: string;
    /** Per-app appearance binding. See {@link CheckboxDialect}. */
    dialect?: CheckboxDialect;
}

/**
 * A checkbox with a real native control and an optional per-dialect drawn box.
 *
 * @param props - {@link CheckboxProps}
 * @returns The rendered checkbox.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
    { checked, indeterminate = false, className, rootClassName, dialect, ...rest },
    ref,
) {
    const inputRef = useRef<HTMLInputElement | null>(null);

    // Merge our own ref with the forwarded one — the effect below needs the node,
    // and consumers (RHF registration, focus management) need it too.
    const setRefs = useCallback(
        (node: HTMLInputElement | null) => {
            inputRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
        },
        [ref],
    );

    // `checked` is in the dep list on purpose: React reuses the same input node
    // across a checked-state change, and the browser clears `indeterminate` when
    // `checked` is assigned — so without it a tri-state box would silently drop
    // back to a plain checked state.
    useEffect(() => {
        if (inputRef.current) inputRef.current.indeterminate = indeterminate;
    }, [indeterminate, checked]);

    const Root = dialect?.rootAs ?? 'span';
    const isOn = checked === true || indeterminate;

    return (
        <Root className={clsx(ROOT_STRUCTURE, dialect?.root ?? DEFAULT_ROOT_DISPLAY, rootClassName)}>
            <input
                ref={setRefs}
                type="checkbox"
                checked={checked}
                className={clsx('m-0', dialect?.input, className)}
                {...rest}
            />
            {dialect?.box && (
                /* Decoration beside the real control, never a replacement for it. */
                <span
                    aria-hidden
                    className={clsx(
                        dialect.box,
                        // Exclusive, never additive — see CheckboxDialect.box.
                        isOn ? dialect.boxChecked : dialect.boxUnchecked,
                    )}
                >
                    {checked === true && dialect.checkMark}
                    {indeterminate && checked !== true && <span className={dialect.indeterminateMark} />}
                </span>
            )}
        </Root>
    );
});
